<template>
  <v-dialog
    :model-value="modelValue"
    max-width="500"
    :scrim="true"
    class="confirm-dialog-overlay"
    @update:model-value="$emit('update:modelValue', $event)">
    <v-card class="confirm-dialog" :class="`confirm-dialog--${variant}`">
      <v-card-title class="confirm-dialog__title">{{ title }}</v-card-title>
      <v-card-text class="confirm-dialog__body">
        <p class="confirm-dialog__message">{{ message }}</p>
        <v-textarea
          v-if="showInput"
          v-model="inputValue"
          :label="inputLabel"
          variant="outlined"
          rows="3"
          class="mt-4"
          hide-details />
      </v-card-text>
      <v-card-actions class="confirm-dialog__actions">
        <v-spacer />
        <v-btn variant="text" :disabled="loading" @click="onCancel">{{ cancelText }}</v-btn>
        <v-btn :color="confirmColor" :loading="loading" @click="onConfirm">
          {{ confirmText }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, computed } from "vue";

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true,
  },
  title: {
    type: String,
    required: true,
  },
  message: {
    type: String,
    required: true,
  },
  confirmText: {
    type: String,
    default: "Confirm",
  },
  cancelText: {
    type: String,
    default: "Cancel",
  },
  variant: {
    type: String,
    default: "default",
    validator: (v) => ["default", "warning", "danger"].includes(v),
  },
  showInput: {
    type: Boolean,
    default: false,
  },
  inputLabel: {
    type: String,
    default: "",
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:modelValue", "confirm", "cancel"]);

const inputValue = ref("");

const confirmColor = computed(() => {
  if (props.variant === "danger") return "error";
  if (props.variant === "warning") return "warning";
  return "primary";
});

function onConfirm() {
  emit("confirm", props.showInput ? inputValue.value : undefined);
}

function onCancel() {
  inputValue.value = "";
  emit("cancel");
  emit("update:modelValue", false);
}
</script>

<style lang="scss" scoped>
@import "@/styles/_variables.scss";

.confirm-dialog {
  border-radius: 16px !important;
  box-shadow: $lh_shadow_lg !important;
  overflow: hidden;
}

.confirm-dialog--danger {
  border-left: 4px solid $lh_color_red;
}

.confirm-dialog--warning {
  border-left: 4px solid $lh_color_orange;
}

.confirm-dialog__title {
  font-size: $lh_font_size_large;
  font-weight: $lh_font_weight_bold;
  padding: 20px 24px 8px;
}

.confirm-dialog__body {
  padding: 8px 24px 16px;
}

.confirm-dialog__message {
  font-size: $lh_body1_size;
  color: $lh_color_navy_dark;
  line-height: 1.5;
}

.confirm-dialog__actions {
  padding: 8px 24px 20px;
}
</style>

<style lang="scss">
.confirm-dialog-overlay .v-overlay__scrim {
  backdrop-filter: blur(4px);
}
</style>
